import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX, Radio } from 'lucide-react';
import { PageHeroBanner } from '@/components/common/PageHeroBanner';
import { SEO } from '@/components/common/SEO';

export const RadioPage = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [isLoading, setIsLoading] = useState(false);
  const audioRef = useRef(null);

  const STREAM_URL = 'https://streaming.live365.com/a06887';

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        setIsLoading(true);
        audioRef.current.play()
          .then(() => {
            setIsPlaying(true);
            setIsLoading(false);
          })
          .catch((err) => {
            console.error('Playback error:', err);
            setIsLoading(false);
          });
      }
    }
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
    if (newVolume === 0) {
      setIsMuted(true);
    } else if (isMuted) {
      setIsMuted(false);
    }
  };

  return (
    <div data-testid="radio-page">
      <SEO 
        title="Radio"
        description="Listen to New Logical Radio for live sports commentary, match updates, and sports talk covering soccer, GAA, hurling, and rugby."
        url="/radio"
      />
      <PageHeroBanner 
        pageSlug="radio" 
        fallbackTitle="New Logical Radio"
        fallbackSubtitle="Live sports commentary and updates"
      />
      
      <div className="min-h-screen py-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Main Radio Player */}
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl p-8 md:p-12 border border-gray-700 shadow-2xl">
            {/* Logo and Title */}
            <div className="text-center mb-8">
              <div className="w-32 h-32 mx-auto mb-6 bg-gradient-to-br from-sky-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg shadow-sky-500/30">
                <Radio className="w-16 h-16 text-white" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                New Logical Radio
              </h1>
              <p className="text-gray-400 text-lg">Live Sports Broadcasting</p>
            </div>

            {/* Visualizer Animation (when playing) */}
            {isPlaying && (
              <div className="flex items-end justify-center gap-1 h-16 mb-8">
                {[...Array(20)].map((_, i) => (
                  <div
                    key={i}
                    className="w-2 bg-gradient-to-t from-sky-500 to-blue-400 rounded-full"
                    style={{
                      animation: `equalizer 0.5s ease-in-out infinite alternate`,
                      animationDelay: `${i * 0.05}s`,
                      height: `${Math.random() * 100}%`,
                    }}
                  />
                ))}
              </div>
            )}

            {/* Play Button */}
            <div className="flex justify-center mb-8">
              <button
                onClick={togglePlay}
                disabled={isLoading}
                className={`w-24 h-24 rounded-full flex items-center justify-center transition-all transform hover:scale-105 ${
                  isPlaying 
                    ? 'bg-red-500 hover:bg-red-600' 
                    : 'bg-sky-500 hover:bg-sky-600'
                } shadow-lg ${isPlaying ? 'shadow-red-500/30' : 'shadow-sky-500/30'}`}
                data-testid="radio-play-btn"
              >
                {isLoading ? (
                  <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin" />
                ) : isPlaying ? (
                  <Pause className="w-10 h-10 text-white" />
                ) : (
                  <Play className="w-10 h-10 text-white ml-1" />
                )}
              </button>
            </div>

            {/* Status */}
            <div className="text-center mb-8">
              <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
                isPlaying 
                  ? 'bg-green-500/20 text-green-400' 
                  : 'bg-gray-700 text-gray-400'
              }`}>
                <span className={`w-2 h-2 rounded-full ${isPlaying ? 'bg-green-400 animate-pulse' : 'bg-gray-500'}`} />
                {isPlaying ? 'Now Playing' : 'Click to Play'}
              </span>
            </div>

            {/* Volume Control */}
            <div className="flex items-center justify-center gap-4 max-w-xs mx-auto">
              <button onClick={toggleMute} className="text-gray-400 hover:text-white transition-colors">
                {isMuted || volume === 0 ? (
                  <VolumeX className="w-6 h-6" />
                ) : (
                  <Volume2 className="w-6 h-6" />
                )}
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={isMuted ? 0 : volume}
                onChange={handleVolumeChange}
                className="flex-1 h-2 bg-gray-700 rounded-full appearance-none cursor-pointer accent-sky-500"
              />
            </div>

            {/* Hidden Audio Element */}
            <audio
              ref={audioRef}
              src={STREAM_URL}
              preload="none"
            />
          </div>

          {/* About Section */}
          <div className="mt-12 bg-gray-800/50 rounded-xl p-6 border border-gray-700">
            <h2 className="text-xl font-semibold text-white mb-4">About New Logical Radio</h2>
            <p className="text-gray-300 mb-4">
              New Logical Radio brings you live sports commentary, match updates, and behind-the-scenes content 
              from grassroots and semi-professional sports across the East Coast.
            </p>
            <p className="text-gray-400 text-sm">
              Perfect for when you're on the go and can't watch the stream. Listen to live commentary 
              for all our major broadcasts.
            </p>
          </div>

          {/* Schedule Preview */}
          <div className="mt-8 bg-gray-800/50 rounded-xl p-6 border border-gray-700">
            <h2 className="text-xl font-semibold text-white mb-4">Upcoming Broadcasts</h2>
            <p className="text-gray-400">
              Check our fixtures page for upcoming matches with live radio commentary.
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes equalizer {
          0% { height: 20%; }
          100% { height: 100%; }
        }
      `}</style>
    </div>
  );
};

export default RadioPage;
